'use client';

import React, { useMemo, useState, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
    Building2,
    Search,
    ChevronLeft,
    ChevronRight,
    ArrowUpDown,
    ArrowUp,
    ArrowDown,
    Handshake,
    CreditCard,
    Wifi,
    WifiOff,
    AlertTriangle,
    Sparkles,
    ChevronDown,
    CalendarClock,
    ScrollText,
    ArrowRight,
} from 'lucide-react';
import { PARTNER_ADMIN_ROUTES } from '@/config/router';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Progress } from '@/components/ui/progress';
import { Skeleton } from '@/components/ui/skeleton';
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { DateRangePicker, getParisCurrentWeek, formatParisDate } from '@/components/ui/date-range-picker';
import {
    BarChart,
    Bar,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    Legend,
    ResponsiveContainer,
} from 'recharts';
import type { DateRange } from 'react-day-picker';
import { trpc } from '@/lib/api/trpc';
import { formatMoney } from '@/lib/money';
import { usePartnerAdmin } from '@/hooks/use-partner-admin';
import { useUrlDateRange } from '@/hooks/use-url-date-range';

// ============================================================================
// Helpers
// ============================================================================

const fmtCents = (cents: number) =>
    `$${(cents / 100).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const fmtDollars = (dollars: number) =>
    `$${dollars.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const fmtCompact = (n: number) =>
    n >= 1_000_000
        ? `$${(n / 1_000_000).toFixed(1)}M`
        : n >= 10_000
            ? `$${(n / 1000).toFixed(0)}k`
            : `$${n.toFixed(0)}`;

function getApprovalRateColor(rate: number): string {
    if (rate >= 60) return 'text-emerald-600';
    if (rate >= 30) return 'text-amber-600';
    return 'text-red-600';
}

function computeWeeks(range: DateRange): number {
    if (!range.from || !range.to) return 12;
    const diffMs = range.to.getTime() - range.from.getTime();
    return Math.max(1, Math.ceil(diffMs / (1000 * 60 * 60 * 24 * 7)));
}

function getPeriodNoun(range: DateRange): 'week' | 'month' | 'period' {
    if (!range.from || !range.to) return 'week';
    const days = (range.to.getTime() - range.from.getTime()) / (1000 * 60 * 60 * 24);
    if (days <= 10) return 'week';
    if (days <= 35) return 'month';
    return 'period';
}

// ============================================================================
// Hero — "Partner Total Earnings" + breakdown
// ============================================================================

function EarningsHero({
    partnerLabel,
    combinedCents,
    crmShareCents,
    processingShareDollars,
    progressPct,
    periodNoun,
    isLoading,
    monthlyScale,
    showProcessing,
    isBuyRate,
}: {
    partnerLabel: string;
    combinedCents: number;
    crmShareCents: number;
    processingShareDollars: number;
    progressPct: number;
    periodNoun: 'week' | 'month' | 'period';
    isLoading: boolean;
    /**
     * 30.4375 days/mo ÷ period_duration_days. Comes from
     * `data.mrr.scaleFactor` so the hero MRR matches what the
     * backend computes for the platform (no double conversions).
     */
    monthlyScale: number;
    /** CRM-only partner → hide the Processing breakdown segment. */
    showProcessing: boolean;
    /** Buy-rate partner → CRM commission is the markup (sell − buy). */
    isBuyRate: boolean;
}) {
    const projection = progressPct > 0 && progressPct < 100
        ? Math.round(combinedCents * (100 / progressPct))
        : combinedCents;
    // Run-rate MRR for the partner = partner end-of-period earnings × scale.
    const mrrCents = Math.round(projection * monthlyScale);
    // Low confidence: under 10% elapsed, the projection multiplier is ≥10×
    // and a single early sale dominates the extrapolation.
    const lowConfidence = progressPct > 0 && progressPct < 10;

    return (
        <div className="rounded-xl border bg-gradient-to-br from-purple-50/60 via-background to-background dark:from-purple-950/20 p-6">
            <div className="flex flex-wrap items-end justify-between gap-6">
                <div>
                    <p className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
                        {partnerLabel} earnings, this {periodNoun}
                    </p>
                    {isLoading ? (
                        <Skeleton className="mt-2 h-10 w-48" />
                    ) : (
                        <p className="mt-1 font-semibold tabular-nums text-4xl text-purple-700 dark:text-purple-400">
                            {fmtCents(combinedCents)}
                        </p>
                    )}
                    {!isLoading && (
                        <p className="mt-2 text-sm text-muted-foreground">
                            {isBuyRate ? 'CRM markup' : 'CRM'} <span className="font-medium text-foreground">{fmtCents(crmShareCents)}</span>
                            {showProcessing && (
                                <>
                                    <span className="mx-2 text-muted-foreground/40">·</span>
                                    Processing <span className="font-medium text-foreground">{fmtDollars(processingShareDollars)}</span>
                                </>
                            )}
                        </p>
                    )}
                </div>
                <div className="flex flex-col items-end gap-1">
                    {progressPct < 100 && !isLoading && (
                        <p className="text-sm text-muted-foreground">
                            Tracking to{' '}
                            <span className="font-semibold text-foreground tabular-nums">
                                {fmtCents(projection)}
                            </span>{' '}
                            by end of {periodNoun}
                            {lowConfidence && (
                                <span
                                    className="ml-2 text-xs text-amber-700 dark:text-amber-500"
                                    title={`Only ${progressPct.toFixed(1)}% of period elapsed — projection extrapolates by ${(100 / Math.max(0.01, progressPct)).toFixed(0)}× and is highly sensitive to early sales`}
                                >
                                    • low confidence
                                </span>
                            )}
                        </p>
                    )}
                    {!isLoading && monthlyScale > 0 && (
                        <p className="text-sm text-muted-foreground">
                            ≈{' '}
                            <span className="font-semibold text-foreground tabular-nums">
                                {fmtCents(mrrCents)}
                            </span>{' '}
                            MRR
                            <span
                                className="ml-1.5 text-xs text-muted-foreground/70"
                                title={`Run-rate: ${progressPct < 100 ? 'projected end-of-period' : 'this period\'s'} earnings × ${monthlyScale.toFixed(3)} (= 30.4375 days/mo ÷ ${(30.4375 / monthlyScale).toFixed(2)} days/period)`}
                            >
                                (run-rate)
                            </span>
                        </p>
                    )}
                    {!isLoading && (
                        <div className="flex items-center gap-2">
                            <Progress value={progressPct} className="h-1.5 w-32" />
                            <span className="text-xs text-muted-foreground tabular-nums w-10 text-right">
                                {progressPct.toFixed(0)}%
                            </span>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}

// ============================================================================
// Stat row — denser than MetricCard, used for CRM + Processing
// ============================================================================

function StatRow({
    label,
    value,
    subtitle,
    accent,
}: {
    label: string;
    value: string;
    subtitle?: string;
    accent?: 'partner' | 'tagada' | 'cost';
}) {
    const accentClasses =
        accent === 'partner'
            ? 'text-purple-700 dark:text-purple-400'
            : accent === 'tagada'
                ? 'text-blue-700 dark:text-blue-400'
                : accent === 'cost'
                    ? 'text-red-600 dark:text-red-400'
                    : 'text-foreground';
    return (
        <div className="flex flex-col gap-0.5 px-4 py-3 first:pl-0 last:pr-0">
            <span className="text-xs font-medium uppercase tracking-wide text-muted-foreground">{label}</span>
            <span className={`text-xl font-semibold tabular-nums ${accentClasses}`}>{value}</span>
            {subtitle && <span className="text-xs text-muted-foreground">{subtitle}</span>}
        </div>
    );
}

function StatGroup({
    title,
    icon: Icon,
    description,
    children,
}: {
    title: string;
    icon: React.ComponentType<{ className?: string }>;
    description?: string;
    children: React.ReactNode;
}) {
    return (
        <section className="space-y-3">
            <div className="flex items-baseline gap-3">
                <h2 className="flex items-center gap-2 text-base font-semibold">
                    <Icon className="h-4 w-4 text-muted-foreground" />
                    {title}
                </h2>
                {description && <p className="text-sm text-muted-foreground">{description}</p>}
            </div>
            <div className="rounded-lg border divide-x divide-border flex flex-wrap [&>div]:flex-1 [&>div]:min-w-[140px]">
                {children}
            </div>
        </section>
    );
}

// ============================================================================
// Revenue chart
// ============================================================================

function RevenueOverTimeChart({
    data,
    isLoading,
    showProcessing = true,
}: {
    data: Array<{
        periodStart: string;
        tagadaRevenue: {
            tagadaCustomerFeesCents: number;
            morGrossFeesDollars: number;
            // Null on partner-scoped queries (platform stripe fees not
            // attributable per-partner). The chart falls back to gross.
            morNetFeesCents: number | null;
            totalRevenueCents: number;
        };
    }>;
    isLoading: boolean;
    /** CRM-only partner → hide the Processing bar. */
    showProcessing?: boolean;
}) {
    const chartData = useMemo(
        () =>
            data.map((week) => {
                const start = new Date(week.periodStart);
                const label = start.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
                // Partner panels: morNetFeesCents is null (Q6 Option C),
                // so use gross app-fee dollars as the processing series.
                const processingDollars = week.tagadaRevenue.morNetFeesCents !== null
                    ? week.tagadaRevenue.morNetFeesCents / 100
                    : week.tagadaRevenue.morGrossFeesDollars;
                return {
                    week: label,
                    'CRM Fees': week.tagadaRevenue.tagadaCustomerFeesCents / 100,
                    Processing: processingDollars,
                };
            }),
        [data],
    );

    if (isLoading) return <Skeleton className="h-[280px] w-full" />;
    if (chartData.length === 0) {
        return (
            <div className="text-muted-foreground flex h-[280px] items-center justify-center text-sm">
                No revenue history
            </div>
        );
    }

    return (
        <ResponsiveContainer width="100%" height={280}>
            <BarChart data={chartData} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" className="stroke-muted" vertical={false} />
                <XAxis dataKey="week" tick={{ fontSize: 11 }} className="text-muted-foreground" />
                <YAxis
                    tick={{ fontSize: 11 }}
                    className="text-muted-foreground"
                    tickFormatter={(v: number) => fmtCompact(v)}
                    width={50}
                />
                <Tooltip
                    formatter={(value: number, name: string) => [
                        `$${value.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`,
                        name,
                    ]}
                    labelFormatter={(label: string) => `Week of ${label}`}
                    contentStyle={{ borderRadius: '8px', fontSize: '13px' }}
                />
                <Legend wrapperStyle={{ fontSize: '12px' }} />
                <Bar dataKey="CRM Fees" stackId="rev" fill="#3b82f6" radius={showProcessing ? undefined : [4, 4, 0, 0]} />
                {showProcessing && <Bar dataKey="Processing" stackId="rev" fill="#a855f7" radius={[4, 4, 0, 0]} />}
            </BarChart>
        </ResponsiveContainer>
    );
}

// ============================================================================
// Merchants table — includes ALL partner accounts (active + dormant)
// ============================================================================

type MerchantRow = {
    accountId: string;
    orgName: string | null;
    orgSlug: string | null;
    createdAt: string | null;
    daysSinceCreated: number;
    deletionFlagged: boolean;
    hasCurrentActivity: boolean;
    approvedCount: number;
    declinedCount: number;
    approvalRate: number | null;
    estimatedFeesCents: number;
    totalVolumeDollars: number;
    processingVolumeDollars: number;
    processingAppFeesDollars: number;
    processingTxnCount: number;
    partnerCrmShareCents: number;
    partnerProcessingShareDollars: number;
};

type MerchantSortField =
    | 'orgName'
    | 'totalVolumeDollars'
    | 'approvedCount'
    | 'approvalRate'
    | 'estimatedFeesCents'
    | 'processingAppFeesDollars'
    | 'partnerEarnings';
type SortDir = 'asc' | 'desc';
type StatusFilter = 'all' | 'active' | 'idle' | 'new';

const PAGE_SIZE = 25;

function SortIcon({ field, sortField, sortDir }: { field: MerchantSortField; sortField: MerchantSortField; sortDir: SortDir }) {
    if (field !== sortField) return <ArrowUpDown className="ml-1 inline h-3 w-3 opacity-30" />;
    return sortDir === 'asc'
        ? <ArrowUp className="ml-1 inline h-3 w-3" />
        : <ArrowDown className="ml-1 inline h-3 w-3" />;
}

function MerchantStatusBadge({ row }: { row: MerchantRow }) {
    if (row.deletionFlagged) {
        return (
            <Badge variant="outline" className="border-red-300 text-red-700 dark:border-red-800 dark:text-red-400">
                <AlertTriangle className="mr-1 h-3 w-3" /> Deletion
            </Badge>
        );
    }
    if (row.hasCurrentActivity) {
        return (
            <Badge variant="outline" className="border-emerald-300 text-emerald-700 dark:border-emerald-800 dark:text-emerald-400">
                <Wifi className="mr-1 h-3 w-3" /> Active
            </Badge>
        );
    }
    if (row.daysSinceCreated < 14) {
        return (
            <Badge variant="outline" className="border-amber-300 text-amber-700 dark:border-amber-800 dark:text-amber-400">
                <Sparkles className="mr-1 h-3 w-3" /> New
            </Badge>
        );
    }
    return (
        <Badge variant="outline" className="text-muted-foreground">
            <WifiOff className="mr-1 h-3 w-3" /> Idle
        </Badge>
    );
}

function MerchantsTable({
    merchants,
    isLoading,
    isSuperAdmin,
    showProcessing = true,
    isBuyRate = false,
}: {
    merchants: MerchantRow[];
    isLoading: boolean;
    isSuperAdmin: boolean;
    /** CRM-only partner → hide the Processing Fees column. */
    showProcessing?: boolean;
    /** Buy-rate partner → "Your Share" is the CRM markup (sell − buy). */
    isBuyRate?: boolean;
}) {
    const [search, setSearch] = useState('');
    const [statusFilter, setStatusFilter] = useState<StatusFilter>('all');
    const [sortField, setSortField] = useState<MerchantSortField>('partnerEarnings');
    const [sortDir, setSortDir] = useState<SortDir>('desc');
    const [page, setPage] = useState(0);

    const toggleSort = useCallback((field: MerchantSortField) => {
        setSortField((prev) => {
            if (prev === field) {
                setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
                return field;
            }
            setSortDir('desc');
            return field;
        });
        setPage(0);
    }, []);

    const filtered = useMemo(() => {
        const q = search.toLowerCase();
        let rows = merchants;
        if (statusFilter !== 'all') {
            rows = rows.filter((m) => {
                if (statusFilter === 'active') return m.hasCurrentActivity;
                if (statusFilter === 'idle') return !m.hasCurrentActivity && m.daysSinceCreated >= 14 && !m.deletionFlagged;
                if (statusFilter === 'new') return m.daysSinceCreated < 14 && !m.deletionFlagged;
                return true;
            });
        }
        if (q) {
            rows = rows.filter(
                (a) =>
                    (a.orgName ?? '').toLowerCase().includes(q) ||
                    (a.orgSlug ?? '').toLowerCase().includes(q) ||
                    a.accountId.toLowerCase().includes(q),
            );
        }
        const sorted = [...rows].sort((a, b) => {
            if (sortField === 'orgName') {
                const cmp = (a.orgName ?? '').localeCompare(b.orgName ?? '');
                return sortDir === 'asc' ? cmp : -cmp;
            }
            let av: number, bv: number;
            if (sortField === 'partnerEarnings') {
                av = a.partnerCrmShareCents / 100 + a.partnerProcessingShareDollars;
                bv = b.partnerCrmShareCents / 100 + b.partnerProcessingShareDollars;
            } else if (sortField === 'approvalRate') {
                av = a.approvalRate ?? -1;
                bv = b.approvalRate ?? -1;
            } else {
                av = (a as any)[sortField] ?? 0;
                bv = (b as any)[sortField] ?? 0;
            }
            return sortDir === 'asc' ? av - bv : bv - av;
        });
        return sorted;
    }, [merchants, search, statusFilter, sortField, sortDir]);

    const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
    const pageRows = filtered.slice(page * PAGE_SIZE, (page + 1) * PAGE_SIZE);
    const thClass = 'cursor-pointer select-none hover:bg-muted/50 transition-colors';

    const counts = useMemo(() => {
        const out = { all: merchants.length, active: 0, idle: 0, new: 0 };
        for (const m of merchants) {
            if (m.deletionFlagged) continue;
            if (m.hasCurrentActivity) out.active++;
            else if (m.daysSinceCreated < 14) out.new++;
            else out.idle++;
        }
        return out;
    }, [merchants]);

    return (
        <Card>
            <CardHeader>
                <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                    <div>
                        <CardTitle className="text-base">Merchants</CardTitle>
                        <CardDescription>
                            All sub-accounts under your partnership, including dormant ones
                        </CardDescription>
                    </div>
                    <div className="flex items-center gap-2">
                        <div className="relative">
                            <Search className="text-muted-foreground absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2" />
                            <Input
                                placeholder="Search merchants..."
                                value={search}
                                onChange={(e) => { setSearch(e.target.value); setPage(0); }}
                                className="w-[220px] pl-9"
                            />
                        </div>
                    </div>
                </div>
                <div className="mt-3 flex flex-wrap gap-1.5">
                    {([
                        ['all', 'All', counts.all],
                        ['active', 'Active', counts.active],
                        ['new', 'New', counts.new],
                        ['idle', 'Idle', counts.idle],
                    ] as const).map(([key, label, n]) => (
                        <Button
                            key={key}
                            variant={statusFilter === key ? 'default' : 'outline'}
                            size="sm"
                            className="h-7 px-3 text-xs"
                            onClick={() => { setStatusFilter(key as StatusFilter); setPage(0); }}
                        >
                            {label}
                            <span className="ml-1.5 text-muted-foreground">{n}</span>
                        </Button>
                    ))}
                    <span className="ml-auto self-center text-xs text-muted-foreground">
                        Showing {filtered.length} of {merchants.length}
                    </span>
                </div>
            </CardHeader>
            <CardContent>
                {isLoading ? (
                    <div className="space-y-2">
                        {[1, 2, 3, 4, 5].map((i) => <Skeleton key={i} className="h-10 w-full" />)}
                    </div>
                ) : merchants.length === 0 ? (
                    <p className="text-muted-foreground py-8 text-center text-sm">No merchants tagged yet</p>
                ) : (
                    <>
                        <div className="overflow-x-auto">
                            <Table>
                                <TableHeader>
                                    <TableRow>
                                        <TableHead className={thClass} onClick={() => toggleSort('orgName')}>
                                            Merchant <SortIcon field="orgName" sortField={sortField} sortDir={sortDir} />
                                        </TableHead>
                                        <TableHead>Status</TableHead>
                                        <TableHead className={`text-right ${thClass}`} onClick={() => toggleSort('totalVolumeDollars')}>
                                            CRM Volume <SortIcon field="totalVolumeDollars" sortField={sortField} sortDir={sortDir} />
                                        </TableHead>
                                        <TableHead className={`text-right ${thClass}`} onClick={() => toggleSort('approvalRate')}>
                                            Approval <SortIcon field="approvalRate" sortField={sortField} sortDir={sortDir} />
                                        </TableHead>
                                        <TableHead className={`text-right ${thClass}`} onClick={() => toggleSort('estimatedFeesCents')}>
                                            CRM Fees <SortIcon field="estimatedFeesCents" sortField={sortField} sortDir={sortDir} />
                                        </TableHead>
                                        {showProcessing && (
                                            <TableHead className={`text-right ${thClass}`} onClick={() => toggleSort('processingAppFeesDollars')}>
                                                Processing Fees <SortIcon field="processingAppFeesDollars" sortField={sortField} sortDir={sortDir} />
                                            </TableHead>
                                        )}
                                        <TableHead className={`text-right ${thClass} text-purple-700 dark:text-purple-400`} onClick={() => toggleSort('partnerEarnings')}>
                                            {isBuyRate ? 'Your Markup' : 'Your Share'} <SortIcon field="partnerEarnings" sortField={sortField} sortDir={sortDir} />
                                        </TableHead>
                                    </TableRow>
                                </TableHeader>
                                <TableBody>
                                    {pageRows.map((m) => {
                                        const yourShare = m.partnerCrmShareCents / 100
                                            + (showProcessing ? m.partnerProcessingShareDollars : 0);
                                        const ratePct = (m.approvalRate ?? 0) * 100;
                                        return (
                                            <TableRow key={m.accountId}>
                                                <TableCell>
                                                    <div className="flex flex-col">
                                                        <span className="text-sm font-medium">
                                                            {m.orgName ?? m.accountId.slice(0, 18)}
                                                        </span>
                                                        <span className="text-muted-foreground font-mono text-xs">
                                                            {m.orgSlug ?? m.accountId}
                                                        </span>
                                                    </div>
                                                </TableCell>
                                                <TableCell>
                                                    <MerchantStatusBadge row={m} />
                                                </TableCell>
                                                <TableCell className="text-right text-sm tabular-nums">
                                                    {m.totalVolumeDollars > 0
                                                        ? `$${m.totalVolumeDollars.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`
                                                        : <span className="text-muted-foreground/60">—</span>}
                                                </TableCell>
                                                <TableCell className={`text-right tabular-nums text-sm ${m.approvalRate !== null ? getApprovalRateColor(ratePct) : 'text-muted-foreground/60'}`}>
                                                    {m.approvalRate !== null ? `${ratePct.toFixed(0)}%` : '—'}
                                                </TableCell>
                                                <TableCell className="text-right tabular-nums text-sm">
                                                    {m.estimatedFeesCents > 0
                                                        ? formatMoney(m.estimatedFeesCents, 'USD')
                                                        : <span className="text-muted-foreground/60">—</span>}
                                                </TableCell>
                                                {showProcessing && (
                                                    <TableCell className="text-right tabular-nums text-sm">
                                                        {m.processingAppFeesDollars > 0
                                                            ? fmtDollars(m.processingAppFeesDollars)
                                                            : <span className="text-muted-foreground/60">—</span>}
                                                    </TableCell>
                                                )}
                                                <TableCell className="text-right font-semibold tabular-nums text-purple-700 dark:text-purple-400">
                                                    {yourShare > 0
                                                        ? fmtDollars(yourShare)
                                                        : <span className="text-muted-foreground/40 font-normal">—</span>}
                                                </TableCell>
                                            </TableRow>
                                        );
                                    })}
                                </TableBody>
                            </Table>
                        </div>

                        {totalPages > 1 && (
                            <div className="mt-4 flex items-center justify-between">
                                <span className="text-muted-foreground text-sm">
                                    Page {page + 1} of {totalPages}
                                </span>
                                <div className="flex items-center gap-2">
                                    <Button
                                        variant="outline"
                                        size="sm"
                                        onClick={() => setPage((p) => Math.max(0, p - 1))}
                                        disabled={page === 0}
                                    >
                                        <ChevronLeft className="mr-1 h-4 w-4" /> Previous
                                    </Button>
                                    <Button
                                        variant="outline"
                                        size="sm"
                                        onClick={() => setPage((p) => Math.min(totalPages - 1, p + 1))}
                                        disabled={page >= totalPages - 1}
                                    >
                                        Next <ChevronRight className="ml-1 h-4 w-4" />
                                    </Button>
                                </div>
                            </div>
                        )}
                    </>
                )}
            </CardContent>
        </Card>
    );
}

// ============================================================================
// Deal terms — collapsible
// ============================================================================

function DealTermsCollapsible({
    data,
    showProcessing = true,
    crmModel = 'revenue_share',
}: {
    data: any;
    showProcessing?: boolean;
    crmModel?: 'buy_rate' | 'revenue_share';
}) {
    const [open, setOpen] = useState(false);
    if (!data) return null;
    const deal = data.deal;
    const isSuperAdmin = data.isSuperAdmin === true;
    const isBuyRate = crmModel === 'buy_rate';

    return (
        <details
            className="group rounded-lg border bg-muted/30"
            open={open}
            onToggle={(e) => setOpen((e.target as HTMLDetailsElement).open)}
        >
            <summary className="flex cursor-pointer list-none items-center justify-between px-4 py-3">
                <div className="flex items-center gap-2 text-sm">
                    <Handshake className="h-4 w-4 text-muted-foreground" />
                    <span className="font-medium">Deal terms — {deal.partnerLabel}</span>
                    <span className="text-muted-foreground">
                        · {isBuyRate ? 'CRM buy rate + markup' : `${deal.crm.partnerSharePct}% CRM`}
                        {showProcessing && `, ${deal.processing.partnerSharePct}% of processing spread`}
                    </span>
                </div>
                <ChevronDown className="h-4 w-4 text-muted-foreground transition-transform group-open:rotate-180" />
            </summary>
            <div className={`grid gap-4 border-t px-4 py-4 text-sm ${showProcessing ? 'sm:grid-cols-2' : ''}`}>
                <div className="space-y-1">
                    <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">CRM Fees</p>
                    {isBuyRate ? (
                        <p>
                            {deal.partnerLabel} keeps the <span className="font-semibold">markup</span> (sell − buy) on top of Tagada&apos;s signed buy rate
                        </p>
                    ) : (
                        <p>
                            {deal.partnerLabel} receives <span className="font-semibold">{deal.crm.partnerSharePct}%</span> of paid CRM subscription fees
                        </p>
                    )}
                    <p className="text-xs text-muted-foreground">
                        Billed weekly to merchants for using the Tagada platform
                    </p>
                </div>
                {showProcessing && (
                <div className="space-y-1">
                    <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">Processing (TagadaPayment)</p>
                    {isSuperAdmin && (
                        <>
                            <p>
                                <span className="text-muted-foreground">Merchant rate:</span>{' '}
                                <span className="font-semibold">
                                    {(deal.processing.merchantRateBps / 100).toFixed(1)}% + ${(deal.processing.merchantFixedCents / 100).toFixed(2)}
                                </span>
                                /txn
                            </p>
                            <p>
                                <span className="text-muted-foreground">Buy rate:</span>{' '}
                                <span className="font-semibold">
                                    {(deal.processing.buyRateBps / 100).toFixed(1)}% + ${(deal.processing.buyRateFixedCents / 100).toFixed(2)}
                                </span>
                                /txn
                            </p>
                        </>
                    )}
                    <p>
                        <span className="text-muted-foreground">Spread:</span>{' '}
                        <span className="font-semibold">
                            {(deal.processing.spreadRateBps / 100).toFixed(1)}% + ${(deal.processing.spreadFixedCents / 100).toFixed(2)}
                        </span>
                        /txn
                    </p>
                    <p>
                        <span className="text-muted-foreground">{deal.partnerLabel} share:</span>{' '}
                        <span className="font-semibold">{deal.processing.partnerSharePct}%</span> of spread
                    </p>
                </div>
                )}
            </div>
        </details>
    );
}

// ============================================================================
// Main page
// ============================================================================

export default function PartnerAdminRevenuePage() {
    const { partner: partnerParam } = useParams<{ partner: string }>();
    const navigate = useNavigate();
    const { partner, canView } = usePartnerAdmin(partnerParam);
    const [dateRange, setDateRange] = useUrlDateRange(getParisCurrentWeek);
    const periodStart = dateRange.from;
    const periodEnd = dateRange.to;

    const weeks = useMemo(() => computeWeeks(dateRange), [dateRange]);
    const periodNoun = useMemo(() => getPeriodNoun(dateRange), [dateRange]);

    const { data, isLoading, error } = trpc.admin.partnerRevenueEstimate.useQuery(
        { partner: partner ?? undefined, periodStart, periodEnd },
        { staleTime: 2 * 60 * 1000, refetchOnWindowFocus: false, enabled: canView && !!partner },
    );
    const { data: historyData, isLoading: historyLoading } = trpc.admin.partnerRevenueHistory.useQuery(
        { partner: partner ?? undefined, weeks: Math.min(weeks, 52), periodStart: periodStart ?? undefined, periodEnd: periodEnd ?? undefined },
        { staleTime: 5 * 60 * 1000, refetchOnWindowFocus: false, enabled: canView && !!partner },
    );
    const { data: dealSplitData, isLoading: dealSplitLoading } = trpc.admin.partnerDealSplit.useQuery(
        { partner: partner ?? undefined, periodStart, periodEnd },
        { staleTime: 5 * 60 * 1000, refetchOnWindowFocus: false, enabled: canView && !!partner },
    );
    const { data: merchantsData, isLoading: merchantsLoading } = trpc.admin.partnerMerchants.useQuery(
        { partner: partner ?? undefined, periodStart, periodEnd },
        { staleTime: 2 * 60 * 1000, refetchOnWindowFocus: false, enabled: canView && !!partner },
    );
    // Latest weekly self-billing statement (cash-basis amount actually owed).
    const { data: latestStatements } = trpc.partnerReports.list.useQuery(
        { partner: partner ?? undefined, limit: 1 },
        { staleTime: 5 * 60 * 1000, refetchOnWindowFocus: false, enabled: canView && !!partner },
    );
    const latestStatement = latestStatements?.[0];

    if (!canView) return null;

    if (error) {
        return (
            <Card className="border-destructive">
                <CardContent className="py-8 text-center">
                    <p className="text-destructive font-medium">Failed to load revenue</p>
                    <p className="text-muted-foreground mt-1 text-sm">{error.message}</p>
                </CardContent>
            </Card>
        );
    }

    const tagadaRevenue = data?.tagadaRevenue;
    const globalTotals = data?.globalTotals;
    const deal = dealSplitData?.deal;
    const isSuperAdmin = dealSplitData?.isSuperAdmin === true;
    const partnerLabel = deal?.partnerLabel ?? (partner ?? '').toUpperCase();

    // Processing section visibility is driven by whether the partner
    // ACTUALLY earns on processing — i.e. has a legacy deal with a
    // non-zero spread or share. This is independent of subscribed_products:
    // a partner can be marked `['crm']` while still on the legacy
    // processing-spread model (its billing_deal carries the spread), and
    // we must not hide the revenue they still earn. Once migrated to
    // CRM-only (billing_deal cleared → no processing deal) the section
    // hides itself. `payfac` / empty stays a fallback for partners without
    // a resolved deal.
    const subscribedProducts: string[] = dealSplitData?.subscribedProducts ?? [];
    const proc = deal?.processing;
    const hasProcessingDeal =
        !!proc &&
        ((proc.spreadRateBps ?? 0) > 0 ||
            (proc.spreadFixedCents ?? 0) > 0 ||
            (proc.partnerSharePct ?? 0) > 0);
    // Buy-rate (migrated) partners have no processing spread — their Stripe
    // sub-accounts were reset to Standard at migration — so force-hide the
    // Processing section regardless of the subscribed-products heuristic.
    const isBuyRatePartner = !!data?.partnerBuyRate?.hasActiveBuyRate;
    const showProcessing =
        !isBuyRatePartner &&
        (hasProcessingDeal || subscribedProducts.length === 0 || subscribedProducts.includes('payfac'));
    const showCrm = subscribedProducts.length === 0 || subscribedProducts.includes('crm');

    // CRM commission model. Buy-rate partners earn the MARKUP (sell − buy),
    // surfaced live by `partnerRevenueEstimate.partnerBuyRate`; legacy
    // partners keep the revenue-share % path.
    const buyRate = data?.partnerBuyRate ?? null;
    const isBuyRate = !!buyRate?.hasActiveBuyRate;

    // Migration cutoff (transition-period hint). Only partners with a
    // scheduled/effective CRM cutoff carry a value — everyone else stays
    // undefined and the banner is never shown. We parse defensively (guard
    // against a bad/absent date) and, crucially, describe the model using the
    // server's own cutoff-aware `crmModel` for the viewed period rather than
    // re-deriving it client-side, so the banner can never contradict the
    // figures below as more partners migrate.
    const cutoffRaw = dealSplitData?.crmMigrationCutoffAt ?? null;
    const cutoffDate = cutoffRaw ? new Date(cutoffRaw) : null;
    const hasCutoff = !!cutoffDate && !Number.isNaN(cutoffDate.getTime());
    const periodModel = dealSplitData?.crmModel;

    // Reconcile (legacy only): when the period is in-progress, use the live
    // estimate for the partner share; once billing reports settle, use paid.
    const inProgress = data ? data.progressPercentage < 100 : false;
    const liveEstimateCents = globalTotals?.estimatedFees ?? 0;
    const useEstimate = !isBuyRate && !!dealSplitData && inProgress && dealSplitData.crm.paidCents === 0 && liveEstimateCents > 0;
    const crmShareCents = isBuyRate
        ? buyRate!.totalMarkupCents
        : !dealSplitData
            ? 0
            : useEstimate
                ? Math.round(liveEstimateCents * ((deal?.crm.partnerSharePct ?? 0) / 100))
                : dealSplitData.crm.partnerShareCents;
    const processingShareDollars = showProcessing ? (dealSplitData?.processing.partnerEarningsDollars ?? 0) : 0;
    const combinedCents = crmShareCents + Math.round(processingShareDollars * 100);
    // Tagada's CRM slice. Buy-rate: Tagada keeps the buy fees. Legacy: the
    // % complement (tagadaShareCents only exists in the superadmin variant).
    const tagadaCrmCents = isBuyRate
        ? (isSuperAdmin ? buyRate!.totalBuyFeesCents : 0)
        : !dealSplitData || !isSuperAdmin
            ? 0
            : useEstimate
                ? liveEstimateCents - crmShareCents
                : ('tagadaShareCents' in dealSplitData.crm ? dealSplitData.crm.tagadaShareCents : 0);

    return (
        <div className="space-y-6">
            {/* Date picker strip */}
            <div className="flex flex-wrap items-center justify-between gap-3">
                <DateRangePicker value={dateRange} onChange={setDateRange} />
                <span className="text-muted-foreground text-xs">
                    {periodStart ? formatParisDate(periodStart, { month: 'short', day: 'numeric', year: 'numeric' }) : ''}
                    {' — '}
                    {periodEnd ? formatParisDate(periodEnd, { month: 'short', day: 'numeric', year: 'numeric' }) : ''}
                    {' '}Paris time
                </span>
            </div>

            {/* Transition-period hint — shown only when a migration cutoff exists */}
            {hasCutoff && (
                <div className="flex items-center gap-1.5 rounded-md border border-amber-200 bg-amber-50 px-2.5 py-1.5 text-xs text-amber-800 dark:border-amber-900/40 dark:bg-amber-950/30 dark:text-amber-300">
                    <CalendarClock className="h-3.5 w-3.5 shrink-0" />
                    <span>
                        Transition period · CRM cutoff{' '}
                        <span className="font-medium">
                            {formatParisDate(cutoffDate!, { month: 'short', day: 'numeric', year: 'numeric' })}
                        </span>
                        {' — '}
                        {periodModel === 'buy_rate'
                            ? 'this period is priced on the new buy-rate model'
                            : 'this period is priced on the legacy model'}
                    </span>
                </div>
            )}

            {/* Cash-basis clarification + latest statement shortcut.
                This page shows the LIVE / ESTIMATED revenue (billed + projected);
                the amount actually owed is settled weekly on collected invoices
                and lives in the Statements tab. */}
            <div className="flex flex-col gap-2 rounded-md border bg-muted/30 px-3 py-2.5 text-xs sm:flex-row sm:items-center sm:justify-between">
                <div className="flex items-start gap-1.5 text-muted-foreground">
                    <ScrollText className="mt-0.5 h-3.5 w-3.5 shrink-0" />
                    <span>
                        This view shows <span className="font-medium text-foreground">estimated</span> revenue
                        (fees billed &amp; projected). What you are <span className="font-medium text-foreground">actually owed</span> is
                        settled weekly on <span className="font-medium text-foreground">collected</span> invoices — see Statements.
                    </span>
                </div>
                <div className="flex items-center gap-3 self-end sm:self-auto">
                    {latestStatement && (
                        <span className="whitespace-nowrap text-muted-foreground">
                            Latest owed:{' '}
                            <span className="font-semibold text-emerald-600">
                                {formatMoney(latestStatement.totalOwedCents, 'USD')}
                            </span>
                        </span>
                    )}
                    <Button
                        variant="outline"
                        size="sm"
                        className="h-7 shrink-0 gap-1 text-xs"
                        onClick={() => partner && navigate(PARTNER_ADMIN_ROUTES(partner).REPORTS)}
                    >
                        Statements <ArrowRight className="h-3 w-3" />
                    </Button>
                </div>
            </div>

            {/* Hero */}
            <EarningsHero
                partnerLabel={partnerLabel}
                combinedCents={combinedCents}
                crmShareCents={crmShareCents}
                processingShareDollars={processingShareDollars}
                progressPct={data?.progressPercentage ?? 0}
                periodNoun={periodNoun}
                isLoading={isLoading || dealSplitLoading}
                monthlyScale={data?.mrr?.scaleFactor ?? 0}
                showProcessing={showProcessing}
                isBuyRate={isBuyRate}
            />

            {/* CRM Fees */}
            {showCrm && (
            <StatGroup
                title="CRM Fees"
                icon={Building2}
                description="Subscription fees billed to your merchants for using the Tagada platform"
            >
                <StatRow
                    label="Billed"
                    value={globalTotals ? formatMoney(globalTotals.estimatedFees, 'USD') : '—'}
                    subtitle={globalTotals && globalTotals.enhancedFees > 0
                        ? `incl. enhanced ${formatMoney(globalTotals.enhancedFees, 'USD')}`
                        : undefined}
                />
                <StatRow
                    label={data && data.progressPercentage >= 100 ? 'Period total' : `Projected end of ${periodNoun}`}
                    value={data ? formatMoney(data.projectedCrmRevenueCents, 'USD') : '—'}
                    subtitle={data
                        ? data.progressPercentage >= 100
                            ? 'period completed'
                            : data.lowConfidenceProjection
                                ? `${data.progressPercentage.toFixed(0)}% elapsed · low confidence`
                                : `${data.progressPercentage.toFixed(0)}% of ${periodNoun} elapsed`
                        : undefined}
                />
                <StatRow
                    label="MRR (run-rate)"
                    value={data?.mrr ? formatMoney(data.mrr.crmCents, 'USD') : '—'}
                    subtitle={data?.mrr
                        ? `× ${data.mrr.scaleFactor.toFixed(2)} from this ${periodNoun}`
                        : undefined}
                />
                <StatRow
                    label="Active accounts"
                    value={globalTotals?.activeAccounts.toString() ?? '—'}
                    subtitle={globalTotals
                        ? `global approval rate ${globalTotals.approvalRate.toFixed(1)}%`
                        : undefined}
                />
                {deal && (
                    <StatRow
                        label={isBuyRate
                            ? `${partnerLabel} markup`
                            : `${partnerLabel} share (${deal.crm.partnerSharePct}%)`}
                        value={fmtCents(crmShareCents)}
                        subtitle={isBuyRate
                            ? 'sell − buy · live'
                            : useEstimate ? 'live estimate' : 'paid'}
                        accent="partner"
                    />
                )}
                {isSuperAdmin && deal && (
                    <StatRow
                        label={isBuyRate ? 'Tagada (buy rate)' : `Tagada (${100 - deal.crm.partnerSharePct}%)`}
                        value={fmtCents(tagadaCrmCents)}
                        accent="tagada"
                    />
                )}
            </StatGroup>
            )}

            {/* Processing */}
            {showProcessing && dealSplitData && deal && (() => {
                const proc = dealSplitData.processing;
                // The superadmin variant of the processing slice has
                // gross/stripe/tagada fields the partner-only response
                // omits. We render them conditionally; the `in` guard
                // also lets TS narrow the union safely.
                const procFull = isSuperAdmin && 'grossAppFeeDollars' in proc ? proc : null;
                return (
                    <StatGroup
                        title="Processing fees"
                        icon={CreditCard}
                        description="Application fees collected on each card payment processed by your merchants through TagadaPayment"
                    >
                        <StatRow
                            label="Volume"
                            value={fmtDollars(proc.totalVolumeDollars)}
                            subtitle={`${proc.transactionCount.toLocaleString()} txns`}
                        />
                        {procFull && (
                            <StatRow
                                label="Gross app fees"
                                value={fmtDollars(procFull.grossAppFeeDollars)}
                            />
                        )}
                        {procFull && (
                            <StatRow
                                label="Stripe cost"
                                value={fmtDollars(procFull.stripeProcessingCostDollars)}
                                accent="cost"
                            />
                        )}
                        <StatRow
                            label={`${partnerLabel} share (${deal.processing.partnerSharePct}% of spread)`}
                            value={fmtDollars(proc.partnerEarningsDollars)}
                            subtitle={proc.totalVolumeDollars > 0
                                ? `${(proc.partnerEarningsDollars / proc.totalVolumeDollars * 100).toFixed(2)}% of volume`
                                : undefined}
                            accent="partner"
                        />
                        {procFull && (
                            <StatRow
                                label="Tagada (net)"
                                value={fmtDollars(procFull.tagadaEarningsDollars)}
                                accent="tagada"
                            />
                        )}
                    </StatGroup>
                );
            })()}

            {/* Revenue chart */}
            <Card>
                <CardHeader className="pb-3">
                    <div className="flex items-center justify-between">
                        <div>
                            <CardTitle className="text-base">Revenue over time</CardTitle>
                            <CardDescription>
                                Weekly {partnerLabel} CRM{showProcessing ? ' + processing' : ''} fees, last {Math.min(weeks, 52)} weeks
                            </CardDescription>
                        </div>
                    </div>
                </CardHeader>
                <CardContent>
                    <RevenueOverTimeChart
                        data={(historyData ?? []) as any}
                        isLoading={historyLoading}
                        showProcessing={showProcessing}
                    />
                </CardContent>
            </Card>

            {/* Merchants */}
            <MerchantsTable
                merchants={(merchantsData?.merchants ?? []) as MerchantRow[]}
                isLoading={merchantsLoading}
                isSuperAdmin={isSuperAdmin}
                showProcessing={showProcessing}
                isBuyRate={isBuyRate}
            />

            {/* Deal terms — collapsed at the bottom */}
            {dealSplitData && <DealTermsCollapsible data={dealSplitData} showProcessing={showProcessing} crmModel={isBuyRate ? 'buy_rate' : 'revenue_share'} />}
        </div>
    );
}
